


























import { computed, defineComponent, onBeforeUnmount, PropType, ref, watch } from "@vue/composition-api";
import { isEqual } from "lodash-es";
import { HomeFeedItem, InputHomeFeedSearch } from "@scrile/api-provider/dist/projects/scrileConnect/HomeFeedProvider";
import useAuthUser from "@/hooks/useAuthUser";
import useBreakPoints from "@/hooks/useBreakPoints";
import useNSFWUserSettings from "@/hooks/useNSFWUserSettings";
import usePager from "@/hooks/usePager";
import useUserFollowings from "@/hooks/useUserFollowings";
import providers from "@/lib/api";
import { homeFeedFields } from "@/lib/fieldsHelper";
import BasePage from "@/components/BasePage";
import BaseSpinner from "@/components/BaseSpinner";
import CreatorsSlider from "@/components/CreatorsSlider";
import EmptyContent from "@/components/EmptyContent";
import InfiniteScroll from "@/components/InfiniteScroll";
import HomeFeedCard from "./components/HomeFeedCard";
import { HOME_FEED_PAGE_SIZE, HOME_FEED_SORTS } from "./const";

const isHoverDevice = () => !!window.matchMedia?.("(hover: hover)").matches;
const getItemKey = (item: HomeFeedItem) => `${item.userId}_${item.setId || 0}`;

export default defineComponent({
  name: "PageHome",
  components: { CreatorsSlider, BaseSpinner, EmptyContent, HomeFeedCard, InfiniteScroll, BasePage },
  props: {
    tags: { type: Array as PropType<string[]>, default: () => [] },
    languages: { type: Array as PropType<string[]>, default: () => [] },
    locations: { type: Array as PropType<string[]>, default: () => [] },
  },
  setup(props, { root }) {
    const { authUser, isAnyCreator } = useAuthUser();
    const { showNotSafeForWork } = useNSFWUserSettings();
    const { portraitUp } = useBreakPoints();

    const loading = ref(true);
    const itemKeyOnCenterLine = ref<string | null>(null);

    const canSortByDistance = computed(() => isAnyCreator.value && root.$store.state.hasLocation);
    // the trailers are not safe for work, so only a logged in user can watch them
    const canPlayTrailers = computed(() => !!authUser.value && showNotSafeForWork.value);
    const playTrailerOnHover = computed(() => canPlayTrailers.value && isHoverDevice());
    // without a hover the card in the middle of the screen plays, but only one column has a single card there
    const playCenteredTrailer = computed(() => canPlayTrailers.value && !portraitUp.value && !isHoverDevice());
    const centeredItemKey = computed(() => (playCenteredTrailer.value ? itemKeyOnCenterLine.value : null));
    const requestInput = computed<InputHomeFeedSearch>(() => ({
      sort: canSortByDistance.value ? HOME_FEED_SORTS.CLOSEST : HOME_FEED_SORTS.NEWEST,
      // creators without posts are shown only to other creators
      hasPost: isAnyCreator.value ? undefined : true,
      userTagIds: props.tags.length ? props.tags : undefined,
      locations: props.locations.length ? props.locations.map(i => JSON.parse(i)) : undefined,
      languages: props.languages.length ? props.languages : undefined,
    }));

    const { enableScroll, items, getNextPage, getFirstPage } = usePager<HomeFeedItem, InputHomeFeedSearch>(page =>
      providers.HomeFeedProvider.search(
        { data: { page, size: HOME_FEED_PAGE_SIZE, ...requestInput.value } },
        homeFeedFields({ distance: isAnyCreator.value, subscription: !!authUser.value })
      )
    );

    const { followedUserIds } = useUserFollowings(items);

    let centerObserver: IntersectionObserver | undefined;

    const onCenterChange = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        const key = (entry.target as HTMLElement).dataset.key ?? null;
        if (entry.isIntersecting) itemKeyOnCenterLine.value = key;
        else if (itemKeyOnCenterLine.value === key) itemKeyOnCenterLine.value = null;
      });
    };

    const observeCard = (el: Element) => {
      // the root is narrowed to the horizontal line in the middle of the screen
      centerObserver = centerObserver ?? new IntersectionObserver(onCenterChange, { rootMargin: "-50% 0px -50% 0px" });
      centerObserver.observe(el);
    };

    const unobserveCard = (el: Element) => centerObserver?.unobserve(el);

    const getData = async () => {
      try {
        loading.value = true;
        await getFirstPage();
      } finally {
        loading.value = false;
      }
    };

    // the list depends on the viewer too: blocked creators, subscriptions and followings
    watch(
      () => ({ ...requestInput.value, viewerId: authUser.value?.id }),
      (newValue, oldValue) => {
        if (isEqual(newValue, oldValue)) return;
        getData();
      },
      { immediate: true }
    );

    onBeforeUnmount(() => centerObserver?.disconnect());

    return {
      loading,
      enableScroll,
      items,
      followedUserIds,
      isAnyCreator,
      showNotSafeForWork,
      playTrailerOnHover,
      centeredItemKey,
      getItemKey,
      getNextPage,
      observeCard,
      unobserveCard,
    };
  },
});
